import clsx from 'clsx'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { match } from 'ts-pattern'

import Header from '@/components-latest/layout/Header'
import Banner from '@/components-latest/primitives/Banner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components-latest/primitives/Controls/Select'
import { InfoCircle } from '@/components-latest/primitives/Icons'
import Store from '@/components-latest/primitives/Icons/Store'
import Spinner from '@/components-latest/primitives/Spinner'
import Text, { textVariants } from '@/components-latest/primitives/Text'
import EditMenuItemModal, {
  EDIT_MENU_ITEM_CACHE_KEY
} from '@/components-latest/ui/MenuPage/EditMenuItemModal'
import MenuItem from '@/components-latest/ui/MenuPage/MenuItem'
import MenuSearchToolbar from '@/components-latest/ui/MenuPage/MenuSearchToolbar'
import { SupportedLanguages } from '@/config/i18n'
import {
  useEditMenuItemMutation,
  useFetchMenuListQuery
} from '@/ducks/menu/api/menu.api'
import { IMenuItem } from '@/ducks/menu/menu.types'
import { selectMenuFilters } from '@/ducks/menu/selectors/menu.selectors'
import { useFetchStoreBranchesQuery } from '@/ducks/store/api/store.api'
import {
  selectSelectedBranchId,
  selectStoreId,
  selectStoreName
} from '@/ducks/store/selectors/store.selectors'
import { setSelectedBranchId } from '@/ducks/store/slice/store.slice'

import { checkItemsPresence, getFilteredResults } from './MenuPage.helpers'
import Styles from './MenuPage.module.scss'

const MenuPage = () => {
  const { t, i18n } = useTranslation()
  const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages

  const filters = useSelector(selectMenuFilters)
  const storeId = useSelector(selectStoreId) || ''
  const storeName = useSelector(selectStoreName) || ''

  const { data: branches } = useFetchStoreBranchesQuery()
  const selectedBranchId = useSelector(selectSelectedBranchId)
  const dispatch = useDispatch()

  const handleBranchChange = (newBranchId: string) => {
    dispatch(setSelectedBranchId(newBranchId))
  }
  const { store_web_app_menu_management } = useFlags()
  const [selectedBranchName, setSelectedBranchName] =
    useState<string>(storeName)
  const fetchMenuQuery = useFetchMenuListQuery(
    {
      store_id: selectedBranchId || storeId
    },
    {
      selectFromResult: ({ data, ...rest }) => {
        const filteredResults = data?.menu
          ? getFilteredResults(data, filters, language)
          : []

        return {
          ...rest,
          data,
          filteredResults: filteredResults,
          hasFiltered: checkItemsPresence(filteredResults),
          hasData: checkItemsPresence(data?.menu?.mainCategories)
        }
      }
    }
  )

  const [activeEditMenuItem, setActiveEditMenuItem] =
    useState<IMenuItem | null>(null)
  const openEditMenuModalCreator = (menuItem: IMenuItem) => () => {
    setActiveEditMenuItem(menuItem)
  }
  const closeEditMenuModal = useCallback(() => {
    setActiveEditMenuItem(null)
  }, [setActiveEditMenuItem])
  const [_, { isLoading }] = useEditMenuItemMutation({
    fixedCacheKey: EDIT_MENU_ITEM_CACHE_KEY
  })
  useEffect(() => {
    const matchedBranch = branches?.find(
      restaurant => restaurant._id === selectedBranchId
    )
    const branchName = matchedBranch?.restaurantname || storeName

    setSelectedBranchName(branchName)
  }, [branches, selectedBranchId, storeName])
  return (
    <>
      <div className={Styles.menuPage}>
        <EditMenuItemModal
          isOpen={!!activeEditMenuItem}
          closeModal={closeEditMenuModal}
          menuItem={activeEditMenuItem}
          storeId={selectedBranchId}
        />
        <Header title={t('common.page-names.menu')}>
          {store_web_app_menu_management &&
            Array.isArray(branches) &&
            branches.length > 0 && (
              <Select
                onValueChange={handleBranchChange}
                value={selectedBranchId || storeId}>
                <SelectTrigger
                  arrowVariant="solidArrow"
                  className={clsx(
                    Styles.selectTrigger,
                    textVariants({ variant: 'nextBody' })
                  )}>
                  <Store />
                  <SelectValue asChild>
                    <Text variant="nextBody">
                      {selectedBranchId === storeId || !selectedBranchId
                        ? storeName
                        : branches.find(
                            branch => branch._id === selectedBranchId
                          )?.restaurantname}
                    </Text>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className={Styles.selectContent}>
                  <SelectItem key="default" value={storeId}>
                    {storeName}
                  </SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch._id} value={branch._id}>
                      {branch.restaurantname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
        </Header>
        {store_web_app_menu_management &&
        Array.isArray(branches) &&
        branches.length > 1 ? (
          <Banner
            description={`You are currently viewing the menu for ${selectedBranchName}`}
            icon={<InfoCircle />}
            type="plain"
            state="info"
          />
        ) : (
          <div style={{ paddingBottom: '16px' }} />
        )}
        <div className={Styles.content}>
          <MenuSearchToolbar />
          {match(fetchMenuQuery)
            .with({ isSuccess: true, hasData: false }, () => (
              <div className={Styles.message}>
                <img
                  className={Styles.imageForEmpty}
                  src="/images/emptyOrders.svg"
                  alt="Waiting for menu"
                />
                <Text variant="title-3" gutter={8}>
                  {t('menu.empty-menu-title')}
                </Text>
                <Text variant="subtitle" className={Styles.dimmed}>
                  {t('menu.empty-menu-message')}
                </Text>
              </div>
            ))
            .with({ isLoading: true }, () => (
              <div className={Styles.message}>
                <Spinner />
              </div>
            ))
            .with(
              { isSuccess: true, hasFiltered: true },
              ({ filteredResults, isFetching }) => {
                return (
                  <>
                    {filteredResults.map(category => (
                      <div
                        className={clsx(Styles.categoryBlock, {
                          [Styles.faded]: isFetching
                        })}
                        key={category._id}>
                        {category.items && category.items.length > 0 && (
                          <div className={Styles.categoryBlock}>
                            <Text
                              className={Styles.categoryTitle}
                              variant="nextBody"
                              weight="semiBold">
                              {category.name[language]}
                            </Text>
                            {category.items.map(item => (
                              <MenuItem
                                disableOffer={openEditMenuModalCreator(item)}
                                key={item._id}
                                menuItem={item}
                                disabled={isLoading}
                              />
                            ))}
                          </div>
                        )}

                        {category?.subCategories?.length > 0 &&
                          category.subCategories.map(subCategory =>
                            subCategory.items?.length > 0 ? (
                              <div
                                className={Styles.categoryBlock}
                                key={subCategory._id}>
                                <Text
                                  className={Styles.categoryTitle}
                                  variant="nextBody"
                                  weight="semiBold">
                                  {`${category.name[language]} / ${subCategory.name[language]}`}
                                </Text>
                                {subCategory.items.map(item => (
                                  <MenuItem
                                    disableOffer={openEditMenuModalCreator(
                                      item
                                    )}
                                    key={item._id}
                                    menuItem={item}
                                    disabled={isLoading}
                                  />
                                ))}
                              </div>
                            ) : null
                          )}
                      </div>
                    ))}
                  </>
                )
              }
            )
            .with({ isSuccess: true, hasFiltered: false }, () => (
              <div className={Styles.message}>
                <img
                  className={Styles.imageForEmpty}
                  src="/images/empty-orders-search.svg"
                  alt="Waiting for in progress orders"
                />
                <Text variant="title-3" gutter={8}>
                  {t('menu.empty-search-title')}
                </Text>
                <Text variant="subtitle" className={Styles.dimmed}>
                  {t('menu.empty-search-message')}
                </Text>
              </div>
            ))
            .otherwise(() => null)}
        </div>
      </div>
    </>
  )
}

export default MenuPage
